import {Fragment, Suspense, useCallback} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {PermissionTypeKr, TripOrderByType} from '@di-shared/api/di/type';
import {ERROR} from '@di-shared/const';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {HistoryDateContentProps} from '@di-shared/features/history/date/HistoryDateContent/index';
import useTripDetailKr from '@di-shared/features/history/date/useTripDetail.kr';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {isSameDate, parseDateWithFormat} from '@shared/dateUtils';
import useIntersectionObserver from '@shared/useHandleObserver';

export default function ({vin, selectedDate, isMonthChange}: HistoryDateContentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
          isMonthChange={isMonthChange}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, isMonthChange}: HistoryDateContentProps) {
  const {isEmptyTrip} = useTripDetailKr({vin, selectedDate, orderBy: TripOrderByType.ASC});

  // 월 변경 로딩화면
  if (isMonthChange) {
    return <FallbackLoading />;
  }

  // No data 화면
  if (isEmptyTrip) {
    return <None selectedDate={selectedDate} />;
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function None({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('T10008')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('T10067')}</span>
        </div>
      </div>
    </>
  );
}

function Default({vin, selectedDate}: {vin: string; selectedDate: Date}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {convertDistanceSymbol, convertDistanceValue, convertSpeedSymbol, convertSpeedValue} = useUnitConverter();
  const {checkAvailableComponent} = useAvailableComponentKr({vin});
  const {fetchNextPage, hasNextPage, isFetchingNextPage, summaryData, totalDrivingHours, totalDrivingMinutes, formattedTripDetails} =
    useTripDetailKr({vin, selectedDate, orderBy: TripOrderByType.ASC});

  // 새로운 주행기록 스크롤
  const handleObserver = useCallback(
    async (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const {observerRef} = useIntersectionObserver(handleObserver, {threshold: 0});

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('T10008')}</span>}
        </div>
        <dl className="driving_summary_list">
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10053')}</dt>
            <dd className="driving_summary_info">
              {!!totalDrivingHours && (
                <>
                  <span className="driving_summary_value">{totalDrivingHours}</span>
                  <span className="driving_summary_unit">{t('T10054')}</span>
                </>
              )}
              {!!totalDrivingMinutes && (
                <>
                  <span className="driving_summary_value">{totalDrivingMinutes}</span>
                  <span className="driving_summary_unit">{t('T10055')}</span>
                </>
              )}
              {!totalDrivingHours && !totalDrivingMinutes && <span className="driving_summary_value">{t('T10202')}</span>}
            </dd>
          </div>
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10052')}</dt>
            <dd className="driving_summary_info">
              {summaryData.totalDrvDist < 1 ? (
                <span className="trip_summary_value">{t('T20341')}</span>
              ) : (
                <>
                  <span className="trip_summary_value">{formatNumberWithCommas(convertDistanceValue(summaryData.totalDrvDist))}</span>
                  <span className="trip_summary_unit">{convertDistanceSymbol(summaryData.totalDrvDist)}</span>
                </>
              )}
            </dd>
          </div>
          {checkAvailableComponent(PermissionTypeKr.SAFETY_SCORE) && (
            <div className="driving_summary_item">
              <dt className="driving_summary_title">{t('T10057')}</dt>
              <dd className="driving_summary_info">
                {summaryData.safetyDrvScoreDiff >= 0 ? (
                  <span className="driving_summary_compare type_up">
                    <span className="ic ic_di_count_up">
                      <span className="blind">{t('T10059')}</span>
                    </span>
                    <span className="driving_summary_value">{formatNumberWithCommas(Math.abs(summaryData.safetyDrvScoreDiff))}</span>
                  </span>
                ) : (
                  <span className="driving_summary_compare type_down">
                    <span className="ic ic_di_count_down">
                      <span className="blind">{t('T10058')}</span>
                    </span>
                    <span className="driving_summary_value">{formatNumberWithCommas(Math.abs(summaryData.safetyDrvScoreDiff))}</span>
                  </span>
                )}
                <span className="driving_summary_value">{formatNumberWithCommas(summaryData.safetyDrvScore)}</span>
                <span className="driving_summary_unit">{t('T10056')}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>
      {formattedTripDetails.map((trip) => {
        const ignitionOnDate = parseDateWithFormat(trip.ignOnTime);
        const ignitionOffDate = parseDateWithFormat(trip.ignOffTime);

        if (!ignitionOnDate || !ignitionOffDate) {
          throw new ExpectedError('ignitionOnDate or ignitionOffDate error');
        }

        return (
          <Fragment key={trip.tripId}>
            <div className="trip_info type_badge">
              <span className="blind">trip info detail</span>
              <div className="trip_info_head">
                <span className="trip_info_item">
                  {DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}
                </span>
                {trip.tags
                  ?.filter(({tagType}) => tagType === 'BUSINESS')
                  .map(({tagId, tagName}) => (
                    <span
                      key={tagId}
                      className="badge badge_basic_container"
                    >
                      {tagName}
                    </span>
                  ))}
              </div>
            </div>
            <div className="statistics_record_area">
              <ul className="statistics_record_list">
                <li className="statistics_record_item">
                  <span className="statistics_record_key">
                    <span className="ic ic_time_16px" />
                    {t('T10053')}
                  </span>
                  <span className="statistics_record_val">
                    {!trip.intervalIgnitionHours && !trip.intervalIgnitionMinutes && (
                      <span className="statistics_record_unit">{t('T10202')}</span>
                    )}
                    {!!trip.intervalIgnitionHours && (
                      <>
                        <span className="statistics_record_number">{trip.intervalIgnitionHours}</span>
                        <span className="statistics_record_unit">{t('T10054')}</span>
                      </>
                    )}
                    {!!trip.intervalIgnitionMinutes && (
                      <>
                        <span className="statistics_record_number">{trip.intervalIgnitionMinutes}</span>
                        <span className="statistics_record_unit">{t('T10055')}</span>
                      </>
                    )}
                  </span>
                </li>
                <li
                  className="statistics_record_item"
                  role="text"
                  aria-label={`${t('T10052')} ${
                    trip.drvDist < 1
                      ? t('T20341')
                      : formatNumberWithCommas(convertDistanceValue(trip.drvDist)) + convertDistanceSymbol(trip.drvDist)
                  }`}
                >
                  <span className="statistics_record_key">
                    <span className="ic ic_drive_distance_16px" />
                    {t('T10052')}
                  </span>
                  <span className="statistics_record_val">
                    {trip.drvDist < 1 ? (
                      <span className="statistics_record_unit">{t('T20341')}</span>
                    ) : (
                      <>
                        <span className="statistics_record_number">{formatNumberWithCommas(convertDistanceValue(trip.drvDist))}</span>
                        <span className="statistics_record_unit">{convertDistanceSymbol(trip.drvDist)}</span>
                      </>
                    )}
                  </span>
                </li>
                <li
                  className="statistics_record_item"
                  role="text"
                  aria-label={`${t('T10205')} ${formatNumberWithCommas(convertSpeedValue(trip.avgSpeed))}${convertSpeedSymbol(trip.avgSpeed)}`}
                >
                  <span className="statistics_record_key">
                    <span className="ic ic_avgspeed_16px" />
                    {t('T10205')}
                  </span>
                  <span className="statistics_record_val">
                    <span className="statistics_record_number">{formatNumberWithCommas(convertSpeedValue(trip.avgSpeed))}</span>
                    <span className="statistics_record_unit">{convertSpeedSymbol(trip.avgSpeed)}</span>
                  </span>
                </li>
              </ul>
              <button
                type="button"
                className="btn_learn_more"
                onClick={() => navigate(ROUTE_PATHS.HISTORY_$TRIP_ID.getFullPath(vin, trip.tripId))}
              >
                {t('T10063')}
                <span className="ic ic_arrow_forward_12px" />
              </button>
            </div>
          </Fragment>
        );
      })}
      {(isFetchingNextPage || hasNextPage) && (
        <div
          className="page_loading_box"
          ref={observerRef}
        >
          <div className="loading_lottie">
            <LottieSelector
              id="img_json_loading_black_60f"
              kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              loop={true}
              autoplay={true}
              style={{width: 24, height: 24}}
            />
          </div>
          <span className="loading_text">{t('T10185')}</span>
        </div>
      )}
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol} = useUnitConverter();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date skeleton">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="trip_date_today">{t('T10008')}</span>
        </div>
        <div className="trip_summary_list type_multi_line type_error skeleton">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10054')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10055')}</span>
            </div>
          </div>
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10057')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10056')}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        className="trip_info skeleton"
        style={{height: '126px'}}
      >
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('T10008')}</span>}
        </div>
        <div className="trip_summary_list type_multi_line type_error">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10054')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10055')}</span>
            </div>
          </div>
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10057')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10056')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}
